import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  OutlinedInput,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TicketService from '../../../services/ticket.service';
import InvitationService from '../../../services/invitation.service';

const styles = {
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '12px', 
      minWidth: '500px'
    }
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #e0e0e0'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    padding: '24px'
  },
  actions: {
    padding: '16px 24px',
    borderTop: '1px solid #e0e0e0'
  },
  submitButton: {
    backgroundColor: 'primary.main',
    borderRadius: '8px',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#333'
    }
  },
  cancelButton: {
    color: '#666',
    borderRadius: '8px',
    textTransform: 'none'
  }
};

function Form({ open, onClose, initialData = null, onSubmit }) {
  const [formData, setFormData] = React.useState({
    ticket_id: '',
    recipient_name: '',
    recipient_email: '',
    recipient_phone: '',
    notes: '',
    statut: 'valid',
    client_id: null
  });

  const [tickets, setTickets] = React.useState([]);

  React.useEffect(() => {
    const fetchTickets = async () => {
      try {
        const ticketsResponse = await TicketService.list();
        setTickets(ticketsResponse);
      } catch (error) {
        console.error('Erreur lors de la récupération des tickets:', error);
      }
    };
    fetchTickets();
  }, []);

  React.useEffect(() => {
    if (initialData) {
      setFormData({
        ticket_id: initialData.ticket?.id || '',
        recipient_name: initialData.recipientName || '',
        recipient_email: initialData.recipientEmail || '',
        recipient_phone: initialData.recipientPhone || '',
        notes: initialData.notes || '',
        statut: initialData.statut || 'valid',
        client_id: initialData.client?.id || null
      });
    } else {
      setFormData({
        ticket_id: '',
        recipient_name: '',
        recipient_email: '',
        recipient_phone: '',
        notes: '',
        statut: 'valid',
        client_id: null
      });
    }
  }, [initialData]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const dataToSubmit = {
      ...formData
    };
    
    if (onSubmit) {
      onSubmit(dataToSubmit);
    } else {
      onClose();
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      sx={styles.dialog}
    >
      <DialogTitle sx={styles.dialogTitle}>
        <span>{initialData ? 'Modifier l\'invitation' : 'Nouvelle invitation'}</span>
        <IconButton 
          onClick={handleClose}
          size="small"
          sx={{ color: '#666' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={styles.form}>
          <TextField
            fullWidth
            select
            label="Ticket"
            name="ticket_id"
            value={formData.ticket_id}
            onChange={handleChange}
            required
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          >
            {tickets.map((ticket) => (
              <MenuItem key={ticket.id} value={ticket.id}>
                {ticket.name} - {ticket.price?.toLocaleString('fr-FR')} FCFA
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            label="Nom du destinataire"
            name="recipient_name"
            value={formData.recipient_name}
            onChange={handleChange}
            required
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          />

          <TextField
            fullWidth
            label="Email du destinataire"
            name="recipient_email"
            type="email"
            value={formData.recipient_email}
            onChange={handleChange}
            required
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          />

          <TextField
            fullWidth
            label="Téléphone du destinataire"
            name="recipient_phone"
            value={formData.recipient_phone}
            onChange={handleChange}
            required
            size="small"
            placeholder="+22900000000"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          />

          <TextField
            fullWidth
            select
            label="Statut"
            name="statut"
            value={formData.statut}
            onChange={handleChange}
            required
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          >
            <MenuItem value="valid">Valide</MenuItem>
            <MenuItem value="invalid">Invalide</MenuItem>
            <MenuItem value="pending">En attente</MenuItem>
          </TextField>

          <TextField
            fullWidth
            label="Notes"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            multiline
            rows={4}
            size="small"
            placeholder="Informations supplémentaires sur l'invitation..."
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          />

        </DialogContent>

        <DialogActions sx={styles.actions}>
          <Button 
            onClick={handleClose}
            sx={styles.cancelButton}
          >
            Annuler
          </Button>
          <Button 
            type="submit"
            variant="contained"
            sx={styles.submitButton}
          >
            {initialData ? 'Modifier' : 'Ajouter'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

Form.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  initialData: PropTypes.shape({
    id: PropTypes.number,
    ticket: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string
    }),
    recipientName: PropTypes.string,
    recipientEmail: PropTypes.string,
    recipientPhone: PropTypes.string,
    notes: PropTypes.string,
    statut: PropTypes.string,
    client: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string
    })
  })
};

export default Form;
